import type { RiskDecomposition } from '@prm/shared';
import { num, pct } from '../format';

/** The full Euler decomposition, behind the Detail toggle. */
export function RiskTable({ risk }: { risk: RiskDecomposition }) {
  return (
    <>
      <div className="panel__body" style={{ paddingBottom: 6 }}>
        <div className="kv">
          <Stat k="Model vol" v={pct(risk.portfolioVol, 2)} note={`${risk.covarianceWindow}d covariance, daily`} />
          <Stat
            k="Measured vol"
            v={pct(risk.realizedVol, 2)}
            note={`${risk.realizedVolWindow ?? '—'}d realized, daily`}
          />
          <Stat k="Coverage" v={pct(risk.coverage)} note="of gross" bad={risk.coverage < 0.9} />
          <Stat
            k="Identity"
            v={risk.identity.closes ? 'closes' : 'BROKEN'}
            note={
              risk.identity.volResidual === null
                ? 'not computed'
                : `Σ RCᵢ − σ_p = ${risk.identity.volResidual.toExponential(1)}`
            }
            bad={!risk.identity.closes && risk.portfolioVol !== null}
          />
        </div>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Ticker</th>
              <th>Weight</th>
              <th title="Daily volatility over the covariance window">Vol</th>
              <th title="Marginal contribution to risk: ∂σ_p/∂wᵢ">MCR</th>
              <th title="wᵢ · MCRᵢ — these sum to σ_p, not to variance">Risk contrib</th>
              <th title="wᵢ · (Σw)ᵢ — these sum to σ_p²">Var contrib</th>
              <th>Risk share</th>
              <th title="Risk contribution per dollar — the trim ranking key">Risk / $</th>
            </tr>
          </thead>
          <tbody>
            {risk.positions.map((p) => (
              <tr key={p.ticker} className={p.isDiversifier ? 'is-diversifier' : ''}>
                <td className="ticker">
                  {p.ticker}
                  {p.isDiversifier ? <span className="chip chip--muted">div</span> : null}
                </td>
                <td className="num">{pct(p.weight)}</td>
                <td className="num dim">{pct(p.volatility, 2)}</td>
                <td className={`num ${p.isDiversifier ? 'up' : ''}`}>{p.mcr === null ? '—' : num(p.mcr, 4)}</td>
                <td className="num">{p.riskContribution === null ? '—' : num(p.riskContribution, 5)}</td>
                <td className="num dim">
                  {p.varianceContribution === null ? '—' : p.varianceContribution.toExponential(2)}
                </td>
                <td className={`num ${(p.riskShare ?? 0) > 0.4 ? 'down' : ''}`}>{pct(p.riskShare)}</td>
                <td className="num dim">{p.riskPerDollar === null ? '—' : p.riskPerDollar.toExponential(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="footnote">
        Euler decomposition off the {risk.covarianceWindow}d covariance matrix. Risk contributions sum to σ_p;
        variance contributions sum to σ_p². Model vol and measured vol use different windows on purpose and are not
        reconciled.
      </div>
    </>
  );
}

function Stat({ k, v, note, bad }: { k: string; v: string; note?: string; bad?: boolean }) {
  return (
    <div className="kv__item">
      <div className="kv__k">{k}</div>
      <div className={`kv__v num ${bad ? 'down' : ''}`}>{v}</div>
      {note ? <div className="faint" style={{ fontSize: 10 }}>{note}</div> : null}
    </div>
  );
}
